import React from 'react';

export const TableSkeleton = () => {
  // Create arrays for skeleton rows and columns - match real table exactly
  const skeletonRows = Array(8).fill(null); // 8 skeleton rows
  // Use realistic question lengths like in the real survey
  const questionLengths = [
    45, // "What is your favorite comedy show on Netflix?" 
    35, // "Rate your love for comedy shows on Netflix"
    42, // "Who is your favorite comedy actor on Netflix?"
    38, // "What type of comedy do you prefer?"
    65, // "On a scale of 1 to 10, how much do you laugh while watching Netflix comedies?"
    52, // "What makes Netflix enjoyable?"
  ];

  return (
    <div className="mx-auto px-8 py-4">
      {/* Toolbar skeleton */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-3">
          {/* Filter buttons skeleton */}
          <div className="flex items-center gap-2">
            <div className="h-9 w-24 bg-gray-100 rounded-lg animate-shimmer-subtle"></div>
            <div className="h-9 w-20 bg-gray-100 rounded-lg animate-shimmer-subtle"></div>
            <div className="h-9 w-48 bg-gray-100 rounded-lg animate-shimmer-subtle"></div>
          </div>
        </div>
        <div className="flex items-center gap-1 border-l border-gray-200 pl-4">
          <div className="h-9 w-9 bg-gray-100 rounded-lg animate-shimmer-subtle"></div>
          <div className="h-9 w-9 bg-gray-100 rounded-lg animate-shimmer-subtle"></div>
        </div>
      </div>

      {/* Table skeleton */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            {/* Header skeleton */}
            <thead>
              <tr className="bg-gray-50/50 text-left text-xs">
                <th className="px-3 py-3 border-b border-r border-gray-100 font-medium w-10">
                  <div className="h-5 w-5 bg-gray-100 rounded-md animate-shimmer-subtle"></div>
                </th>
                <th className="px-3 py-3 border-b border-r border-gray-100 font-medium text-gray-600 min-w-28 text-xs">
                  <div className="h-4 w-12 bg-gray-100 rounded animate-shimmer-subtle"></div>
                </th>
                {questionLengths.map((length, index) => (
                  <th key={index} className={`px-3 py-3 border-b font-medium text-gray-600 text-xs ${
                    index < questionLengths.length - 1 ? 'border-r border-gray-100' : 'border-gray-100'
                  } ${
                    length > 60 ? 'min-w-80 max-w-96' : 
                    length > 30 ? 'min-w-64 max-w-72' : 'min-w-48 max-w-56'
                  }`}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md bg-gray-100 animate-shimmer-subtle flex-shrink-0 shadow-sm"></div>
                      <div className={`bg-gray-100 rounded animate-shimmer-subtle font-medium text-gray-800 leading-tight break-words text-xs ${
                        length > 60 ? 'h-8 w-72' :  // Two lines for very long questions
                        length > 45 ? 'h-6 w-56' :  // Slightly taller for medium-long
                        length > 35 ? 'h-4 w-48' :  // Regular height for medium
                        'h-4 w-40'                  // Regular height for short
                      }`} style={{ animationDelay: `${index * 0.1}s` }}></div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            
            {/* Body skeleton */}
            <tbody>
              {skeletonRows.map((_, rowIndex) => (
                <tr key={rowIndex} className="border-b border-gray-100">
                  {/* Checkbox column */}
                  <td className="px-3 py-2.5 border-r border-gray-100 align-middle">
                    <div className="h-4 w-4 bg-gray-100 rounded-md animate-shimmer-subtle"></div>
                  </td>
                  
                  {/* Date column */}
                  <td className="px-3 py-2.5 border-r border-gray-100 align-middle">
                    <div className="flex flex-col gap-1">
                      <div className="h-3 w-20 bg-gray-100 rounded animate-shimmer-subtle"></div>
                      <div className="h-3 w-16 bg-gray-100 rounded animate-shimmer-subtle"></div>
                    </div>
                  </td>
                  
                  {/* Answer columns - match real data patterns */}
                  {questionLengths.map((length, colIndex) => (
                    <td key={colIndex} className={`px-3 py-2.5 align-middle text-xs ${
                      colIndex < questionLengths.length - 1 ? 'border-r border-gray-100' : ''
                    } ${
                      length > 60 ? 'max-w-96' : 
                      length > 30 ? 'max-w-72' : 'max-w-56'
                    } whitespace-normal break-words leading-relaxed`}>
                      <div className="space-y-1">
                        {/* Different answer patterns based on question type */}
                        {colIndex === 0 && ( // Text question - longer answers
                          <>
                            <div className={`h-3 bg-gray-100 rounded animate-shimmer-subtle ${
                              rowIndex % 4 === 0 ? 'w-20' :
                              rowIndex % 4 === 1 ? 'w-32' :
                              rowIndex % 4 === 2 ? 'w-16' : 'w-28'
                            }`}></div>
                          </>
                        )}
                        {colIndex === 1 && ( // Rating - numbers
                          <div className="h-3 w-4 bg-gray-100 rounded animate-shimmer-subtle"></div>
                        )}
                        {colIndex === 2 && ( // Names
                          <div className={`h-3 bg-gray-100 rounded animate-shimmer-subtle ${
                            rowIndex % 3 === 0 ? 'w-24' :
                            rowIndex % 3 === 1 ? 'w-28' : 'w-20'
                          }`}></div>
                        )}
                        {colIndex === 3 && ( // Categories
                          <div className={`h-3 bg-gray-100 rounded animate-shimmer-subtle ${
                            rowIndex % 4 === 0 ? 'w-20' :
                            rowIndex % 4 === 1 ? 'w-40' :
                            rowIndex % 4 === 2 ? 'w-24' : 'w-32'
                          }`}></div>
                        )}
                        {colIndex === 4 && ( // Scale numbers
                          <div className={`h-3 bg-gray-100 rounded animate-shimmer-subtle ${
                            rowIndex % 3 === 0 ? 'w-6' :
                            rowIndex % 3 === 1 ? 'w-8' : 'w-7'
                          }`}></div>
                        )}
                        {colIndex === 5 && ( // What makes Netflix enjoyable - longer text
                          <div className={`h-3 bg-gray-100 rounded animate-shimmer-subtle ${
                            rowIndex % 4 === 0 ? 'w-32' :
                            rowIndex % 4 === 1 ? 'w-24' :
                            rowIndex % 4 === 2 ? 'w-28' : 'w-20'
                          }`}></div>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};